import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, ArrowLeft, Globe, Pause, Pencil, Play, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, requestWithMessage, type Paginated } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatNumber, formatTime, relativeTime } from '../lib/format';
import {
  MONITOR_STATUSES,
  SERIES_RANGES,
  checkCodeText,
  intervalText,
  kindLabel,
  outageText,
  severityLabel,
  severityTone,
  statusLabel,
  statusTone,
  uptimeText,
  uptimeTone,
  useMonitoringDefaults,
  useServerOptions,
  type MonitorIncidentRow,
  type SeriesRange,
  type UrlDetail,
  type UrlRow,
  type UrlSeries,
} from '../lib/monitoring';
import { MonitorBanner } from '../components/MonitorBanner';
import { ResponseTimeChart, UptimeBars } from '../components/MonitorCharts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

/**
 * Monitoring › URLs (operator, 2026-10-20): the websites, APIs and ports checked
 * on our servers. Each is on one server, and names one of that server's
 * projects or none. The monitor process checks them; these pages read what it
 * found.
 */
export function MonitoringUrlsPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const servers = useServerOptions();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const set = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };

  // "Paused" sits with the statuses, but it is a switch of its own: a paused URL
  // keeps whatever it last showed, and shows Paused.
  const { status, ...rest } = filters;
  const params = {
    ...rest,
    ...(status === 'PAUSED' ? { paused: 'true' } : status ? { status, paused: 'false' } : {}),
  };
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['monitoring', 'urls', page, params],
    queryFn: () => api.get<Paginated<UrlRow>>('/monitoring/urls', { page, pageSize: 50, ...params }),
    refetchInterval: 30_000,
  });

  // The projects to filter by: those on the server picked, or on any server.
  const serverList = servers.data ?? [];
  const projectChoices = [
    ...new Map(
      serverList
        .filter((s) => !filters.serverId || s.id === filters.serverId)
        .flatMap((s) => s.projects)
        .map((p) => [p.id, p]),
    ).values(),
  ];
  const filtered = Object.values(filters).some(Boolean);

  return (
    <>
      <PageHeader
        title={t('monitoring.urls.title')}
        subtitle={t('monitoring.urls.subtitle')}
        count={data?.total}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/monitoring/urls/new')}>
            {t('monitoring.urls.new')}
          </Button>
        }
      />
      <MonitorBanner />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Input placeholder={t('monitoring.urls.search')} value={filters.q ?? ''} onChange={(e) => set('q', e.target.value)} />
          <Select
            value={filters.serverId ?? ''}
            onChange={(e) => setFilters({ ...filters, serverId: e.target.value, projectId: '' })}
          >
            <option value="">{t('monitoring.filter.allServers')}</option>
            {serverList.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
          <Select value={filters.projectId ?? ''} onChange={(e) => set('projectId', e.target.value)}>
            <option value="">{t('monitoring.filter.allProjects')}</option>
            <option value="none">{t('monitoring.url.noProject')}</option>
            {projectChoices.map((p) => (
              <option key={p.id} value={p.id}>{`${p.code} · ${localized(p)}`}</option>
            ))}
          </Select>
          <Select value={filters.status ?? ''} onChange={(e) => set('status', e.target.value)}>
            <option value="">{t('monitoring.filter.allStatuses')}</option>
            {MONITOR_STATUSES.map((s) => (
              <option key={s} value={s}>{t(statusLabel(s))}</option>
            ))}
            <option value="PAUSED">{t('monitoring.status.PAUSED')}</option>
          </Select>
          <Select value={filters.kind ?? ''} onChange={(e) => set('kind', e.target.value)}>
            <option value="">{t('monitoring.filter.allKinds')}</option>
            <option value="HTTP">{t(kindLabel('HTTP'))}</option>
            <option value="TCP">{t(kindLabel('TCP'))}</option>
          </Select>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<Globe className="h-8 w-8" />}
            title={filtered ? undefined : t('monitoring.urls.empty')}
            hint={filtered ? undefined : t('monitoring.urls.emptyHint')}
            action={
              !filtered && (
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/monitoring/urls/new')}>
                  {t('monitoring.urls.new')}
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('monitoring.url.address')}</Th>
                  <Th>{t('monitoring.url.server')}</Th>
                  <Th>{t('common.project')}</Th>
                  <Th>{t('monitoring.url.severity')}</Th>
                  <Th align="end">{t('monitoring.url.response')}</Th>
                  <Th align="end">{t('monitoring.url.uptimeDay')}</Th>
                  <Th>{t('common.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((url) => (
                  <Tr key={url.id} onClick={() => navigate(`/monitoring/urls/${url.id}`)}>
                    <Td>
                      <div className="font-medium">{url.name}</div>
                      <div className="text-caption text-muted">{t(kindLabel(url.kind))}</div>
                    </Td>
                    <Td>
                      <span dir="ltr" className="font-mono text-mono-sm text-[var(--text-secondary)] break-all">{url.url}</span>
                    </Td>
                    <Td className="text-muted">{url.server?.name}</Td>
                    <Td className="text-muted">
                      {url.project ? `${url.project.code} · ${localized(url.project)}` : t('monitoring.url.noProject')}
                    </Td>
                    <Td>
                      <Badge tone={severityTone(url.severity)}>{t(severityLabel(url.severity))}</Badge>
                    </Td>
                    <Td align="end" className="tabular text-muted">
                      {url.lastResponseMs === null ? '—' : t('monitoring.url.ms', { n: formatNumber(url.lastResponseMs, locale) })}
                    </Td>
                    <Td align="end">
                      <Badge tone={uptimeTone(url.uptimeDay)} className="tabular">{uptimeText(url.uptimeDay)}</Badge>
                    </Td>
                    <Td>
                      <UrlStatusBadge url={url} />
                      {url.lastCheckedAt && (
                        <div className="mt-0.5 text-caption text-faint">{relativeTime(url.lastCheckedAt, locale)}</div>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

export function UrlStatusBadge({ url }: { url: Pick<UrlRow, 'status' | 'isPaused'> }) {
  const { t } = useI18n();
  return (
    <Badge tone={statusTone(url.status, url.isPaused)} dot>
      {t(url.isPaused ? 'monitoring.status.PAUSED' : statusLabel(url.status))}
    </Badge>
  );
}

/** Route: `/monitoring/urls/:id`. */
export function MonitoringUrlDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const defaults = useMonitoringDefaults();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [range, setRange] = useState<SeriesRange>('24h');

  const series = useQuery({
    queryKey: ['monitoring', 'url', id, 'series', range],
    queryFn: () => api.get<UrlSeries>(`/monitoring/urls/${id}/series`, { range }),
    refetchInterval: 60_000,
    // The chart keeps what it shows while another range loads: no flash, no jump.
    placeholderData: (previous) => previous,
  });

  const { data: url, isLoading, error, refetch } = useQuery({
    queryKey: ['monitoring', 'url', id],
    queryFn: () => api.get<UrlDetail>(`/monitoring/urls/${id}`),
    // A check every minute at most: what it found shows without a reload.
    refetchInterval: 15_000,
  });

  const afterChange = (data: UrlDetail) => {
    queryClient.setQueryData(['monitoring', 'url', id], data);
    void queryClient.invalidateQueries({ queryKey: ['monitoring', 'urls'] });
    void queryClient.invalidateQueries({ queryKey: ['monitoring', 'server'] });
  };

  const pause = useMutation({
    mutationFn: (paused: boolean) =>
      requestWithMessage<UrlDetail>(`/monitoring/urls/${id}/${paused ? 'pause' : 'resume'}`, { method: 'POST' }),
    onSuccess: ({ data }) => {
      afterChange(data);
      toast.success(t(data.isPaused ? 'monitoring.url.pausedToast' : 'monitoring.url.resumedToast'));
    },
    onError: (e: any) => toast.error(e.message),
  });

  const checkNow = useMutation({
    mutationFn: () => requestWithMessage<UrlDetail>(`/monitoring/urls/${id}/check-now`, { method: 'POST' }),
    onSuccess: ({ data }) => {
      afterChange(data);
      toast.success(t('monitoring.url.checkNowToast'));
      // The monitor looks every few seconds: read again once it has.
      setTimeout(() => void refetch(), 6_000);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/monitoring/urls/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
      toast.success(t('monitoring.url.removed'));
      navigate(url ? `/monitoring/servers/${url.serverId}` : '/monitoring/urls');
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !url) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const d = defaults.data;
  const isHttp = url.kind === 'HTTP';
  const isHttps = isHttp && url.url.startsWith('https://');
  const sslDays = url.sslExpiresAt ? Math.floor((new Date(url.sslExpiresAt).getTime() - Date.now()) / 86_400_000) : null;

  return (
    <>
      <div className="mb-4">
        <Link to="/monitoring/urls" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('monitoring.urls.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>{url.name}</span>
            <UrlStatusBadge url={url} />
          </span>
        }
        subtitle={
          <span dir="ltr" className="font-mono text-mono-sm break-all">
            {url.url}
          </span>
        }
        action={
          <>
            <Button
              icon={<RefreshCw className="h-4 w-4" />}
              loading={checkNow.isPending}
              disabled={url.isPaused}
              onClick={() => checkNow.mutate()}
            >
              {t('monitoring.url.checkNow')}
            </Button>
            <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/monitoring/urls/${id}/edit`)}>
              {t('common.edit')}
            </Button>
            <Button
              icon={url.isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              loading={pause.isPending}
              onClick={() => pause.mutate(!url.isPaused)}
            >
              {t(url.isPaused ? 'monitoring.url.resume' : 'monitoring.url.pause')}
            </Button>
            <Button variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmRemove(true)}>
              {t('monitoring.url.remove')}
            </Button>
          </>
        }
      />
      <MonitorBanner />

      {url.isPaused && (
        <p className="mb-5 rounded-md border hairline bg-[var(--surface-sunken)] px-4 py-3 text-body-sm text-[var(--text-secondary)]">
          {t('monitoring.url.pausedNote')}
        </p>
      )}

      {url.openIncident && (
        <div
          role="alert"
          className="mb-5 flex items-start gap-3 rounded-md border border-error/40 bg-error-bg px-4 py-3 text-body-sm text-error-fg dark:bg-error/10 dark:text-error-dark"
        >
          <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {t('monitoring.url.downSince', {
              when: formatDateTime(url.openIncident.startedAt, locale),
              duration: outageText(url.openIncident.durationSeconds, locale),
              cause: checkCodeText(url.openIncident.causeCode, url.openIncident.causeDetail, t),
            })}
          </span>
        </div>
      )}

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Metric
          label={t('monitoring.url.lastCheck')}
          value={url.lastCheckedAt ? formatTime(url.lastCheckedAt, locale) : '—'}
          hint={
            url.lastCheckedAt
              ? `${relativeTime(url.lastCheckedAt, locale)} · ${
                  url.lastErrorCode
                    ? checkCodeText(url.lastErrorCode, url.lastErrorDetail, t)
                    : url.lastStatusCode
                      ? t('monitoring.url.answered', { code: url.lastStatusCode })
                      : t('monitoring.url.connected')
                }`
              : t('monitoring.status.PENDING')
          }
        />
        <Metric
          label={t('monitoring.url.response')}
          value={url.lastResponseMs === null ? '—' : t('monitoring.url.ms', { n: formatNumber(url.lastResponseMs, locale) })}
          hint={url.statusChangedAt ? t('monitoring.url.statusSince', { when: relativeTime(url.statusChangedAt, locale) }) : undefined}
        />
        <UptimeMetric label={t('monitoring.url.uptimeDay')} percent={url.uptime.day} />
        <UptimeMetric label={t('monitoring.url.uptimeWeek')} percent={url.uptime.week} />
        <UptimeMetric label={t('monitoring.url.uptimeMonth')} percent={url.uptime.month} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('monitoring.url.whatIsChecked')} />
          <dl>
            <Detail label={t('monitoring.url.kind')}>{t(kindLabel(url.kind))}</Detail>
            {isHttp ? (
              <>
                <Detail label={t('monitoring.url.method')}>
                  <span className="font-mono text-mono-sm">{url.method}</span>
                </Detail>
                <Detail label={t('monitoring.url.expectedStatus')}>
                  <span dir="ltr" className="font-mono text-mono-sm">{url.expectedStatus ?? t('monitoring.url.any2xx')}</span>
                </Detail>
                <Detail label={t('monitoring.url.keyword')}>{url.keyword ?? '—'}</Detail>
                <Detail label={t('monitoring.url.headers')}>
                  {url.headers?.length ? (
                    <span dir="ltr" className="font-mono text-mono-sm">{url.headers.map((h) => h.name).join(', ')}</span>
                  ) : (
                    '—'
                  )}
                </Detail>
                {url.method === 'POST' && (
                  <Detail label={t('monitoring.url.body')}>{url.body ? t('common.yes') : t('common.no')}</Detail>
                )}
              </>
            ) : (
              <>
                <Detail label={t('monitoring.url.host')}>
                  <span dir="ltr" className="font-mono text-mono-sm">{url.host}</span>
                </Detail>
                <Detail label={t('monitoring.url.port')}>
                  <span className="font-mono text-mono-sm tabular">{url.port}</span>
                </Detail>
              </>
            )}
          </dl>

          {isHttps && (
            <>
              <CardHeader title={t('monitoring.url.certificate')} className="mb-2 mt-5" />
              <dl>
                <Detail label={t('monitoring.url.expiresOn')}>
                  {url.sslExpiresAt ? (
                    <span className="inline-flex items-center gap-2">
                      {formatDate(url.sslExpiresAt, locale)}
                      {sslDays !== null && (
                        <Badge tone={sslDays <= (url.sslWarnDays ?? d?.sslWarnDays ?? 14) ? 'warning' : 'success'}>
                          {t('monitoring.url.daysLeft', { n: Math.max(0, sslDays) })}
                        </Badge>
                      )}
                    </span>
                  ) : (
                    '—'
                  )}
                </Detail>
                <Detail label={t('monitoring.url.issuer')}>{url.sslIssuer ?? '—'}</Detail>
              </dl>
            </>
          )}
        </Card>

        <Card>
          <CardHeader title={t('monitoring.url.where')} />
          <dl>
            <Detail label={t('monitoring.url.server')}>
              <Link to={`/monitoring/servers/${url.server.id}`} className="hover:text-primary-600 dark:hover:text-primary-400">
                {url.server.name}
              </Link>
            </Detail>
            <Detail label={t('common.project')}>
              {url.project ? `${url.project.code} · ${localized(url.project)}` : t('monitoring.url.noProject')}
            </Detail>
            <Detail label={t('monitoring.url.severity')}>
              <Badge tone={severityTone(url.severity)}>{t(severityLabel(url.severity))}</Badge>
            </Detail>
          </dl>

          <CardHeader title={t('monitoring.url.checks')} className="mb-2 mt-5" />
          <dl>
            <Detail label={t('monitoring.url.interval')}>
              <Own own={url.intervalSeconds} fallback={d?.intervalSeconds} show={(v) => intervalText(v, t)} />
            </Detail>
            <Detail label={t('monitoring.url.timeout')}>
              <Own own={url.timeoutSeconds} fallback={d?.timeoutSeconds} show={(v) => t('monitoring.url.seconds', { n: v })} />
            </Detail>
            <Detail label={t('monitoring.url.failures')}>
              <Own own={url.failuresToConfirm} fallback={d?.failuresToConfirm} show={(v) => formatNumber(v, locale)} />
            </Detail>
            {isHttp && (
              <Detail label={t('monitoring.url.slowMs')}>
                <Own own={url.slowMs} fallback={d?.slowMs} show={(v) => t('monitoring.url.ms', { n: formatNumber(v, locale) })} />
              </Detail>
            )}
            {isHttps && (
              <Detail label={t('monitoring.url.sslWarnDays')}>
                <Own own={url.sslWarnDays} fallback={d?.sslWarnDays} show={(v) => t('monitoring.url.days', { n: v })} />
              </Detail>
            )}
          </dl>
        </Card>
      </div>

      <Card className="mt-5">
        <CardHeader title={t('monitoring.chart.title')} subtitle={t('monitoring.chart.subtitle')} />
        <Tabs
          active={range}
          onChange={(next) => setRange(next as SeriesRange)}
          tabs={SERIES_RANGES.map((r) => ({ id: r, label: t(`monitoring.chart.range.${r}` as TranslationKey) }))}
        />
        <div className={`mt-4 transition-opacity ${series.isFetching ? 'opacity-60' : ''}`}>
          {series.data ? <ResponseTimeChart series={series.data} range={series.data.range} /> : series.error ? <ErrorState error={series.error} /> : <LoadingBlock />}
        </div>
        <div className="mt-6 border-t hairline pt-4">
          <p className="mb-2 text-body-sm font-medium text-[var(--text-secondary)]">{t('monitoring.chart.ninety')}</p>
          {series.data ? <UptimeBars days={series.data.days} /> : null}
        </div>
      </Card>

      <Card className="mt-5" padded={false}>
        <div className="p-5 pb-0">
          <CardHeader title={t('monitoring.url.incidents')} subtitle={t('monitoring.url.incidentsHint')} />
        </div>
        {url.incidents.length === 0 ? (
          <p className="px-5 pb-5 text-body-sm text-muted">{t('monitoring.url.noIncidents')}</p>
        ) : (
          <IncidentsTable incidents={url.incidents} />
        )}
      </Card>

      <Card className="mt-5" padded={false}>
        <div className="p-5 pb-0">
          <CardHeader title={t('monitoring.url.recentChecks')} />
        </div>
        {url.checks.length === 0 ? (
          <p className="px-5 pb-5 text-body-sm text-muted">{t('monitoring.url.noChecks')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('monitoring.url.checkedAt')}</Th>
                <Th>{t('monitoring.url.result')}</Th>
                {isHttp && <Th align="end">{t('monitoring.url.statusCode')}</Th>}
                <Th align="end">{t('monitoring.url.response')}</Th>
                <Th>{t('common.notes')}</Th>
              </tr>
            </thead>
            <tbody>
              {url.checks.map((check) => (
                <Tr key={check.id}>
                  <Td className="whitespace-nowrap tabular text-muted">
                    {formatDate(check.checkedAt, locale)} {formatTime(check.checkedAt, locale)}
                  </Td>
                  <Td>
                    <Badge tone={statusTone(check.result)} dot>{t(statusLabel(check.result))}</Badge>
                  </Td>
                  {isHttp && <Td align="end" className="tabular">{check.statusCode ?? '—'}</Td>}
                  <Td align="end" className="tabular">
                    {check.responseMs === null ? '—' : t('monitoring.url.ms', { n: formatNumber(check.responseMs, locale) })}
                  </Td>
                  <Td className="text-muted">{checkCodeText(check.errorCode, check.errorDetail, t) || '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {confirmRemove && (
        <ConfirmDialog
          title={t('monitoring.url.removeConfirm', { name: url.name })}
          confirmLabel={t('monitoring.url.remove')}
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmRemove(false)}
        >
          <p>{t('monitoring.url.removeBody')}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

function UptimeMetric({ label, percent }: { label: string; percent: number | null }) {
  const tone = uptimeTone(percent);
  return <Metric label={label} value={uptimeText(percent)} tone={tone === 'info' || tone === 'plasma' ? 'neutral' : tone} />;
}

/** A URL's outages: when each began and ended, how long it lasted, why, and how it ended. */
export function IncidentsTable({
  incidents,
  showUrl,
  showServer,
}: {
  incidents: Array<MonitorIncidentRow & { url?: { id: string; name: string; server?: { id: string; name: string } } }>;
  showUrl?: boolean;
  showServer?: boolean;
}) {
  const { t, locale } = useI18n();
  return (
    <Table>
      <thead>
        <tr>
          {showUrl && <Th>{t('monitoring.url.url')}</Th>}
          {showServer && <Th>{t('monitoring.url.server')}</Th>}
          <Th>{t('monitoring.url.started')}</Th>
          <Th>{t('monitoring.url.ended')}</Th>
          <Th align="end">{t('monitoring.url.duration')}</Th>
          <Th>{t('monitoring.url.cause')}</Th>
        </tr>
      </thead>
      <tbody>
        {incidents.map((incident) => (
          <Tr key={incident.id}>
            {showUrl && (
              <Td>
                {incident.url ? (
                  <Link to={`/monitoring/urls/${incident.url.id}`} className="font-medium hover:text-primary-600 dark:hover:text-primary-400">
                    {incident.url.name}
                  </Link>
                ) : (
                  '—'
                )}
              </Td>
            )}
            {showServer && (
              <Td className="text-muted">
                {incident.url?.server ? (
                  <Link to={`/monitoring/servers/${incident.url.server.id}`} className="hover:text-[var(--text-primary)]">
                    {incident.url.server.name}
                  </Link>
                ) : (
                  '—'
                )}
              </Td>
            )}
            <Td className="whitespace-nowrap tabular">{formatDateTime(incident.startedAt, locale)}</Td>
            <Td className="whitespace-nowrap tabular">
              {incident.endedAt ? (
                <span>
                  {formatDateTime(incident.endedAt, locale)}
                  {incident.endReason && incident.endReason !== 'RECOVERED' && (
                    <span className="ms-2 text-caption text-faint">{t(`monitoring.endReason.${incident.endReason}` as TranslationKey)}</span>
                  )}
                </span>
              ) : (
                <Badge tone="error" dot>{t('monitoring.url.ongoing')}</Badge>
              )}
            </Td>
            <Td align="end" className="tabular">{outageText(incident.durationSeconds, locale)}</Td>
            <Td className="text-muted">{checkCodeText(incident.causeCode, incident.causeDetail, t)}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

/** A URL's own figure, or the Monitoring default it takes — marked as such. */
function Own({
  own,
  fallback,
  show,
}: {
  own: number | null;
  fallback: number | undefined;
  show: (value: number) => ReactNode;
}) {
  const { t } = useI18n();
  if (own !== null) return <span className="tabular">{show(own)}</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="tabular">{fallback === undefined ? '—' : show(fallback)}</span>
      <Badge>{t('monitoring.url.default')}</Badge>
    </span>
  );
}
